import { quickJournalChromaFlareTheme } from "./Themes/QuickJournalChromaFlareTheme";
import { quickJournalDuneTheme } from "./Themes/QuickJournalDuneTheme";
import { quickJournalNightTheme } from "./Themes/QuickJournalNightTheme";
import { quickJournalCyberpunkTheme } from "./QuickJournalCyberpunkTheme";
import { LoadQuickJournalAutoTheme } from "./Themes/QuickJournalAutoPreferences";

export const quickJournalThemePresets = {
    night: quickJournalNightTheme,
    dune: quickJournalDuneTheme,
    cyberpunk: quickJournalCyberpunkTheme,
    chromaflare: quickJournalChromaFlareTheme,
} as const;

export type QuickJournalThemeName = keyof typeof quickJournalThemePresets;
export type QuickJournalTheme = (typeof quickJournalThemePresets)[QuickJournalThemeName];
export type QuickJournalThemeSelectionName = QuickJournalThemeName | "auto";

export const quickJournalTheme = quickJournalThemePresets.night;
export const quickJournalThemeStorageKey = "quickJournal_themeName";

export function IsQuickJournalThemeName(
    value: string | null | undefined
): value is QuickJournalThemeSelectionName {
    return (
        value === "night" ||
        value === "dune" ||
        value === "cyberpunk" ||
        value === "chromaflare" ||
        value === "auto"
    );
}

export function NormalizeQuickJournalThemeSelectionName(
    value: string | null | undefined
): QuickJournalThemeSelectionName {
    if (value === "sunbreak") {
        return "dune";
    }

    if (
        value === "night" ||
        value === "dune" ||
        value === "cyberpunk" ||
        value === "chromaflare" ||
        value === "auto"
    ) {
        return value;
    }

    return "night";
}

export function IsQuickJournalSystemDarkMode(): boolean {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return true;
    }

    return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ResolveQuickJournalThemeName(
    themeName?: QuickJournalThemeSelectionName
): QuickJournalThemeName {
    if (!themeName || themeName === "auto") {
        return LoadQuickJournalAutoTheme(IsQuickJournalSystemDarkMode());
    }

    return themeName === "night" ||
        themeName === "dune" ||
        themeName === "cyberpunk" ||
        themeName === "chromaflare"
        ? themeName
        : "night";
}

export function GetQuickJournalTheme(
    theme?: QuickJournalTheme | QuickJournalThemeSelectionName
): QuickJournalTheme {
    if (!theme) {
        return quickJournalThemePresets.night;
    }

    if (typeof theme === "string") {
        return quickJournalThemePresets[ResolveQuickJournalThemeName(theme)];
    }

    return theme;
}

export function GetQuickJournalThemeName(
    theme?: QuickJournalTheme
): QuickJournalThemeName {
    return theme?.id ?? "night";
}

export function LoadStoredQuickJournalThemeName(): QuickJournalThemeSelectionName {
    if (typeof window === "undefined") {
        return "night";
    }

    const storedValue = window.localStorage.getItem(quickJournalThemeStorageKey);
    return NormalizeQuickJournalThemeSelectionName(storedValue);
}
